import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import api from '../../utils/api';
import { GET_CATEGORIES_WITH_COUNT, GET_JOB_HIGHLIGHTS } from '../../config/api';
import {
    AppBar,
    Box,
    Toolbar,
    IconButton,
    Typography,
    Menu,
    Container,
    Button,
    Tooltip,
    MenuItem,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    ListSubheader,
    Divider,
    Paper
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import AdminLoginPopup from '../admin/AdminLoginPopup';
import ChangePasswordModal from './ChangePasswordModal';
import EmailVerifyBanner from './EmailVerifyBanner';
import { FaWhatsapp } from 'react-icons/fa';

const Header = () => {
    const [anchorElUser, setAnchorElUser] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);

    const navigate = useNavigate();
    const { isAuthenticated, currentUser, userRole, logout } = useContext(AuthContext);
    const isLoggedIn = isAuthenticated;
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);

    // ── "Jobs" mega-menu (Naukri-style) ──────────────────────────────
    const [megaOpen, setMegaOpen] = useState(false);
    const [popularCategories, setPopularCategories] = useState([]);
    const [demandJobs, setDemandJobs] = useState([]);
    const [jobLocations, setJobLocations] = useState([]);

    useEffect(() => {
        let active = true;

        // Popular categories (with job counts) — reused from the home page API.
        api.post(GET_CATEGORIES_WITH_COUNT, {})
            .then((res) => {
                const data = res.data?.responseData || res.data;
                const all = Array.isArray(data) ? data : [];
                const cats = all
                    .filter((c) => (c.jobCount || 0) > 0)
                    .sort((a, b) => (b.jobCount || 0) - (a.jobCount || 0))
                    .slice(0, 6)
                    .map((c) => ({
                        id: c.id,
                        name: c.name
                    }));
                if (active) setPopularCategories(cats);
            })
            .catch(() => {});

        // In-demand titles + locations, both derived from the jobs table.
        api.get(GET_JOB_HIGHLIGHTS)
            .then((res) => {
                const data = res.data?.responseData || res.data || {};
                if (active) {
                    setDemandJobs(Array.isArray(data.demandJobs) ? data.demandJobs : []);
                    setJobLocations(Array.isArray(data.locations) ? data.locations : []);
                }
            })
            .catch(() => {});

        return () => { active = false; };
    }, []);

    // Resolve the dashboard path based on user role
    const roleName = userRole?.name;
    const dashboardPath =
        roleName === 'admin' ? '/admin/dashboard' :
        roleName === 'recruiter' || roleName === 'accountOwner' ? '/recruiter/dashboard' :
        '/jobseeker/dashboard';
    const profilePath = roleName === 'jobseeker' ? '/jobseeker/profile' : dashboardPath;
    const [anchorEl, setAnchorEl] = useState(null);
    const open = anchorEl;
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleOpenLogin = () => {
        setLoginOpen(true);
    };

    const StyledMenu = styled((props) => (
        <Menu
            elevation={0}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            {...props}
        />
    ))(({ theme }) => ({
        '& .MuiPaper-root': {
            borderRadius: 6,
            marginTop: theme.spacing(1),
            minWidth: 180,
            color: 'rgb(55, 65, 81)',
            boxShadow:
                'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
            '& .MuiMenu-list': {
                padding: '4px 0',
            },
            '& .MuiMenuItem-root': {
                '& .MuiSvgIcon-root': {
                    fontSize: 18,
                    color: theme.palette.text.secondary,
                    marginRight: theme.spacing(1.5),
                },
                '&:active': {
                    backgroundColor: alpha(
                        theme.palette.primary.main,
                        theme.palette.action.selectedOpacity,
                    ),
                },
            },
            ...theme.applyStyles('dark', {
                color: theme.palette.grey[300],
            }),
        },
    }));

    // Navigation links based on Naukri.com
    // const jobSeekerLinks = [
    //   { name: 'Jobs', href: '/jobs' },
    //   { name: 'Companies', href: '/companies' },
    //   { name: 'Services', href: '/services' },
    // ];

    const jobSeekerLinks = [
        { name: 'Jobs', href: '#latest-jobs' },
        { name: 'Institutions', href: '/jobs-by-institutions' },
        ...(isLoggedIn ? [{ name: 'Dashboard', href: dashboardPath }] : []),
    ];

    // Employer destinations for the mobile drawer. This list is mobile-only —
    // desktop reaches these through the footer and the "Sign In As" menu.
    //
    // It replaces two entries that were both broken: a "FOR EMPLOYERS" row
    // linking to `/employer` and a "Submit Job" row linking to `/post-job`.
    // Neither path is a registered route (see routes/AllRoutes.jsx — the real
    // ones are prefixed `/employer/...`), so both silently dropped mobile
    // visitors on the NotFound page. "FOR EMPLOYERS" was never a destination
    // anyway; it is a category label, so it is now a ListSubheader instead of a
    // tappable row.
    const recruiterLinks = [
        { name: 'Post Jobs', href: '/employer/post-jobs' },
        { name: 'Recruiter Plans', href: '/employer/recruiter-plans' },
    ];

    // Desktop offers Job Seeker / Recruiter / Admin sign-in via the "Sign In As"
    // menu, but the drawer only had a single generic "Login" pointing at /login —
    // which is jobseeker-only (see pages/Login.jsx), so a recruiter had no way to
    // sign in from a phone at all.
    const loginLinks = [
        { name: 'Job Seeker Login', href: '/login?type=jobseeker' },
        { name: 'Recruiter Login', href: '/employer/recruiter-login' },
    ];

    const handleNavClick = (href) => {
        if (href.startsWith('#')) {
            if (window.location.pathname === '/') {
                const element = document.querySelector(href);
                if (element) {
                    const headerHeight = 80;
                    window.scrollTo({ top: element.offsetTop - headerHeight, behavior: 'smooth' });
                }
            } else {
                navigate('/jobs');
            }
        } else {
            navigate(href);
        }
        handleDrawerToggle();
    };

    // Every row is a ListItemButton. They were `<ListItem button="true">`, but
    // ListItem's `button` prop was removed in MUI v6 (this project is on v7), so
    // that string just landed on the DOM as an invalid attribute: no ripple, no
    // hover, and — for the rows driven by onClick rather than a Link — no keyboard
    // focus or button role at all, making them unreachable without a mouse.
    const drawerItemSx = { py: 1.25, px: 2.5 };

    const drawer = (
        <Box sx={{ width: 280, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ p: 2, flexShrink: 0 }}>
                <Link to="/" onClick={handleDrawerToggle} style={{ display: 'inline-flex' }}>
                    <img src="/jeminy_logo.png" alt="Talexo" height={28} />
                </Link>
            </Box>
            <Divider />

            {/* Only this middle section scrolls, so the sign-in rows at the end
                stay reachable on short viewports. */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <List disablePadding>
                    {jobSeekerLinks.map((item) => (
                        <ListItemButton
                            key={item.name}
                            onClick={() => handleNavClick(item.href)}
                            sx={drawerItemSx}
                        >
                            <ListItemText primary={item.name} />
                        </ListItemButton>
                    ))}
                </List>

                <Divider />
                <List
                    disablePadding
                    subheader={
                        <ListSubheader sx={{ lineHeight: '36px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: '#6b7280' }}>
                            FOR EMPLOYERS
                        </ListSubheader>
                    }
                >
                    {recruiterLinks.map((item) => (
                        <ListItemButton
                            key={item.name}
                            component={Link}
                            to={item.href}
                            onClick={handleDrawerToggle}
                            sx={drawerItemSx}
                        >
                            <ListItemText primary={item.name} />
                        </ListItemButton>
                    ))}
                </List>

                {isLoggedIn && (
                    <>
                        <Divider />
                        <List disablePadding>
                            <ListItemButton component={Link} to={dashboardPath} onClick={handleDrawerToggle} sx={drawerItemSx}>
                                <ListItemText primary="Dashboard" />
                            </ListItemButton>
                            <ListItemButton component={Link} to={profilePath} onClick={handleDrawerToggle} sx={drawerItemSx}>
                                <ListItemText primary="My Profile" />
                            </ListItemButton>
                            <ListItemButton onClick={() => { handleDrawerToggle(); setChangePasswordOpen(true); }} sx={drawerItemSx}>
                                <ListItemText primary="Change Password" />
                            </ListItemButton>
                        </List>
                    </>
                )}
            </Box>

            <Divider />
            <List disablePadding sx={{ flexShrink: 0 }}>
                {!isLoggedIn ? (
                    <>
                        {loginLinks.map((item) => (
                            <ListItemButton
                                key={item.name}
                                component={Link}
                                to={item.href}
                                onClick={handleDrawerToggle}
                                sx={drawerItemSx}
                            >
                                <ListItemText primary={item.name} />
                            </ListItemButton>
                        ))}
                        <ListItemButton component={Link} to="/register" onClick={handleDrawerToggle} sx={drawerItemSx}>
                            <ListItemText primary="Register" primaryTypographyProps={{ fontWeight: 600, color: '#0888B8' }} />
                        </ListItemButton>
                    </>
                ) : (
                    <ListItemButton onClick={() => { handleDrawerToggle(); logout(); navigate('/'); }} sx={drawerItemSx}>
                        <ListItemText primary="Logout" primaryTypographyProps={{ color: '#ef4444', fontWeight: 600 }} />
                    </ListItemButton>
                )}
            </List>
        </Box>
    );

    const handleCloseLogin = () => {
        setLoginOpen(false);
    };

    return (
        <>
            <AppBar position="sticky" color="default" elevation={0} className="bg-white">
                <Container maxWidth="xl">
                    <Toolbar disableGutters>
                        {/* Logo - Desktop */}
                        <Typography
                            variant="h5"
                            noWrap
                            component={Link}
                            to="/"
                            sx={{
                                mr: 2,
                                display: { xs: 'none', md: 'flex' },
                                textDecoration: 'none',
                            }}
                        >
                            <img
                                src="/jeminy_logo.png"
                                alt="teacherprofinder.com"
                                className="h-12"
                            />
                        </Typography>

                        {/* Mobile Menu Button */}
                        <Box sx={{ flexGrow: 0, display: { xs: 'flex', md: 'none' } }}>
                            <IconButton
                                size="large"
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleDrawerToggle}
                                sx={{ mr: 2 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>

                        {/* Logo - Mobile */}
                        <Typography
                            variant="h6"
                            noWrap
                            component={Link}
                            to="/"
                            sx={{
                                mr: 2,
                                display: { xs: 'flex', md: 'none' },
                                flexGrow: 1,
                                textDecoration: 'none',
                            }}
                        >
                            <img
                                src="/jeminy_logo.png"
                                alt="teacherprofinder.com"
                                className="h-8"
                            />
                        </Typography>

                        {/* WhatsApp Chat — mobile only, icon-only, right beside the logo — commented out for now */}
                        {/* {!isLoggedIn && (
                            <IconButton
                                component="a"
                                href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi Talexo, I would like to know more about your services.')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Chat with us on WhatsApp"
                                sx={{
                                    display: { xs: 'flex', md: 'none' },
                                    mr: 1,
                                    width: 36,
                                    height: 36,
                                }}
                            >
                                <FaWhatsapp size={22} color="#25D366" />
                            </IconButton>
                        )} */}

                        {/* Desktop Navigation Links */}
                        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                            {jobSeekerLinks.map((link) => {
                                // "Jobs" gets a Naukri-style mega-menu on hover.
                                if (link.name === 'Jobs') {
                                    return (
                                        <Box
                                            key={link.name}
                                            onMouseEnter={() => setMegaOpen(true)}
                                            onMouseLeave={() => setMegaOpen(false)}
                                            sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                                        >
                                            <Button
                                                onClick={() => navigate('/jobs')}
                                                endIcon={
                                                    <KeyboardArrowDownIcon
                                                        sx={{
                                                            fontSize: '18px !important',
                                                            color: '#6b7280',
                                                            transform: megaOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                            transition: 'transform 0.2s'
                                                        }}
                                                    />
                                                }
                                                sx={{
                                                    my: 2, color: 'black', mx: 1, fontWeight: 'normal',
                                                    textTransform: 'none', fontSize: '16px', cursor: 'pointer'
                                                }}
                                            >
                                                {link.name}
                                            </Button>

                                            {megaOpen && (
                                                <Box sx={{ position: 'absolute', top: '100%', left: 0, zIndex: 1300, pt: 1 }}>
                                                    <Paper
                                                        elevation={8}
                                                        sx={{
                                                            p: 3, borderRadius: '12px', display: 'flex', gap: 5,
                                                            boxShadow: '0 12px 32px rgba(0,0,0,0.12)'
                                                        }}
                                                    >
                                                        {/* Popular categories */}
                                                        <Box sx={{ minWidth: 190 }}>
                                                            <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#111827', mb: 1.5 }}>
                                                                Popular categories
                                                            </Typography>
                                                            {popularCategories.length ? popularCategories.map((c) => (
                                                                <Typography
                                                                    key={c.id}
                                                                    component={Link}
                                                                    /*
                                                                     * Filter by the CATEGORY, the way the home page's
                                                                     * category cards already do.
                                                                     *
                                                                     * This used to send every sub-category NAME as a
                                                                     * comma-separated keyword: clicking "Trained Graduate
                                                                     * Teacher (TGT)" produced a URL carrying Arabic, Art
                                                                     * Education, Artificial Intelligence, Athletics,
                                                                     * Badminton … and roughly sixty removable chips on the
                                                                     * results page. The Jobs page splits `keyword` on
                                                                     * commas and ORs the parts across title, description,
                                                                     * department and skills, so it also matched jobs that
                                                                     * merely mention a word — a keyword search dressed up
                                                                     * as a category filter.
                                                                     *
                                                                     * `category` + `categoryName` is what Jobs.jsx already
                                                                     * reads, and it renders as ONE removable chip naming
                                                                     * the category.
                                                                     */
                                                                    to={`/jobs?category=${c.id}&categoryName=${encodeURIComponent(c.name)}`}
                                                                    onClick={() => setMegaOpen(false)}
                                                                    sx={{
                                                                        display: 'block', fontSize: 14, color: '#4b5563',
                                                                        textDecoration: 'none', py: 0.6,
                                                                        '&:hover': { color: '#0AAAE6' }
                                                                    }}
                                                                >
                                                                    {c.name}
                                                                </Typography>
                                                            )) : (
                                                                <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>Loading…</Typography>
                                                            )}
                                                        </Box>

                                                        {/* Jobs in demand (most-posted titles) */}
                                                        <Box sx={{ minWidth: 190 }}>
                                                            <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#111827', mb: 1.5 }}>
                                                                Jobs in demand
                                                            </Typography>
                                                            {demandJobs.length ? demandJobs.map((j, i) => (
                                                                <Typography
                                                                    key={i}
                                                                    component={Link}
                                                                    to={`/jobs?keyword=${encodeURIComponent(j.title)}`}
                                                                    onClick={() => setMegaOpen(false)}
                                                                    sx={{
                                                                        display: 'block', fontSize: 14, color: '#4b5563',
                                                                        textDecoration: 'none', py: 0.6,
                                                                        '&:hover': { color: '#0AAAE6' }
                                                                    }}
                                                                >
                                                                    {j.title}
                                                                </Typography>
                                                            )) : (
                                                                <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>Loading…</Typography>
                                                            )}
                                                        </Box>

                                                        {/* Jobs by location */}
                                                        <Box sx={{ minWidth: 170 }}>
                                                            <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#111827', mb: 1.5 }}>
                                                                Jobs by location
                                                            </Typography>
                                                            {jobLocations.length ? jobLocations.map((loc, i) => (
                                                                <Typography
                                                                    key={i}
                                                                    component={Link}
                                                                    to={`/jobs?location=${encodeURIComponent(loc.name)}`}
                                                                    onClick={() => setMegaOpen(false)}
                                                                    sx={{
                                                                        display: 'block', fontSize: 14, color: '#4b5563',
                                                                        textDecoration: 'none', py: 0.6,
                                                                        '&:hover': { color: '#0AAAE6' }
                                                                    }}
                                                                >
                                                                    Jobs in {loc.name}
                                                                </Typography>
                                                            )) : (
                                                                <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>Loading…</Typography>
                                                            )}
                                                        </Box>
                                                    </Paper>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                }

                                return (
                                    <Button
                                        key={link.name}
                                        onClick={() => {
                                            if (link.href.startsWith('#')) {
                                                if (window.location.pathname === '/') {
                                                    const element = document.querySelector(link.href);
                                                    if (element) {
                                                        const headerHeight = 80;
                                                        window.scrollTo({
                                                            top: element.offsetTop - headerHeight,
                                                            behavior: 'smooth'
                                                        });
                                                    }
                                                } else {
                                                    navigate('/jobs');
                                                }
                                            } else {
                                                navigate(link.href);
                                            }
                                        }}
                                        sx={{
                                            my: 2,
                                            color: 'black',
                                            display: 'block',
                                            mx: 1,
                                            fontWeight: 'normal',
                                            textTransform: 'none',
                                            fontSize: '16px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {link.name}
                                    </Button>
                                );
                            })}

                            {/* WhatsApp Chat — visible only when not logged in — commented out for now */}
                            {/* {!isLoggedIn && (
                                <a
                                    href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi Talexo, I would like to know more about your services.')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        textDecoration: 'none',
                                        color: '#16a34a',
                                        fontWeight: 500,
                                        fontSize: '15px',
                                        padding: '5px 12px',
                                        borderRadius: '8px',
                                        border: '1.5px solid #22c55e',
                                        marginLeft: '8px',
                                        whiteSpace: 'nowrap',
                                        transition: 'background 0.2s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                >
                                    <FaWhatsapp size={18} color="#25D366" />
                                    Chat with us
                                </a>
                            )} */}
                        </Box>

                        {/* User Menu */}
                        <Box sx={{ flexGrow: 0 }}>
                            {!isLoggedIn ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {/* Login & Register — desktop only */}
                                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                                        {/* <Button
                                            component={Link}
                                            to="/login"
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                color: '#1a56db',
                                                border: '1.5px solid #1a56db',
                                                borderRadius: '8px',
                                                px: 2.5,
                                                py: 0.75,
                                                letterSpacing: '0.01em',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    backgroundColor: '#eff6ff',
                                                    borderColor: '#1e40af',
                                                    color: '#1e40af',
                                                    boxShadow: '0 2px 8px rgba(26,86,219,0.15)',
                                                }
                                            }}
                                        >
                                            Login
                                        </Button> */}
                                        <Button
                                            component={Link}
                                            to="/register"
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                color: '#fff',
                                                background: 'linear-gradient(135deg, #0AAAE6 0%, #6428AA 100%)',
                                                borderRadius: '8px',
                                                px: 2.5,
                                                py: 0.75,
                                                letterSpacing: '0.01em',
                                                boxShadow: '0 2px 8px rgba(26,86,219,0.25)',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #0888B8 0%, #4F1F87 100%)',
                                                    boxShadow: '0 4px 14px rgba(26,86,219,0.35)',
                                                    transform: 'translateY(-1px)',
                                                }
                                            }}
                                        >
                                            Register
                                        </Button>
                                    </Box>

                                    {/* FOR EMPLOYERS dropdown */}
                                    <AdminLoginPopup open={loginOpen} onClose={handleCloseLogin} />
                                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                                        <Divider orientation="vertical" flexItem sx={{ mx: 1.5, height: 28, alignSelf: 'center', borderColor: '#e5e7eb' }} />
                                        <Button
                                            id="demo-customized-button"
                                            aria-controls={open ? 'demo-customized-menu' : undefined}
                                            aria-haspopup="true"
                                            aria-expanded={open ? 'true' : undefined}
                                            onClick={handleClick}
                                            startIcon={<BusinessCenterOutlinedIcon sx={{ fontSize: '18px !important', color: '#0AAAE6' }} />}
                                            endIcon={
                                                <KeyboardArrowDownIcon
                                                    sx={{
                                                        fontSize: '18px !important',
                                                        color: '#0888B8',
                                                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.2s',
                                                    }}
                                                />
                                            }
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '13px',
                                                color: '#0888B8',
                                                letterSpacing: '0.03em',
                                                borderRadius: '8px',
                                                px: 1.5,
                                                py: 0.75,
                                                border: '1.5px solid #B8E4F5',
                                                backgroundColor: '#E6F7FD',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    backgroundColor: '#d5f0fa',
                                                    borderColor: '#0AAAE6',
                                                    boxShadow: '0 1px 4px rgba(10,170,230,0.15)',
                                                }
                                            }}
                                        >
                                            Sign In As
                                        </Button>

                                        <StyledMenu
                                            id="demo-customized-menu"
                                            MenuListProps={{
                                                'aria-labelledby': 'demo-customized-button',
                                            }}
                                            anchorEl={anchorEl}
                                            open={open}
                                            onClose={handleClose}
                                        >
                                            <MenuItem
                                                component={Link}
                                                to="/login?type=jobseeker"
                                                onClick={handleClose}
                                                sx={{ fontSize: '14px', fontWeight: 500, color: '#374151', py: 1.2, px: 2, gap: 1.2, '&:hover': { backgroundColor: '#E6F7FD' } }}
                                            >
                                                <PersonOutlineIcon sx={{ fontSize: 18, color: '#0AAAE6' }} />
                                                Job Seeker Login
                                            </MenuItem>
                                            <Divider sx={{ my: 0.5 }} />
                                            <MenuItem
                                                component={Link}
                                                to="/employer/recruiter-login"
                                                onClick={handleClose}
                                                sx={{ fontSize: '14px', fontWeight: 500, color: '#374151', py: 1.2, px: 2, gap: 1.2, '&:hover': { backgroundColor: '#E6F7FD' } }}
                                            >
                                                <BusinessCenterOutlinedIcon sx={{ fontSize: 18, color: '#0AAAE6' }} />
                                                Recruiter Login
                                            </MenuItem>
                                            <Divider sx={{ my: 0.5 }} />
                                            <MenuItem
                                                onClick={() => { handleClose(); handleOpenLogin(); }}
                                                sx={{ fontSize: '14px', fontWeight: 500, color: '#374151', py: 1.2, px: 2, gap: 1.2, '&:hover': { backgroundColor: '#E6F7FD' } }}
                                            >
                                                <LockOutlinedIcon sx={{ fontSize: 18, color: '#0AAAE6' }} />
                                                Admin Login
                                            </MenuItem>
                                        </StyledMenu>
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {/* Notification icons */}
                                        {/* <IconButton sx={{ ml: 1 }}>
                                            <BellIcon />
                                        </IconButton>
                                        <IconButton sx={{ ml: 1 }}>
                                            <ChatIcon />
                                        </IconButton> */}

                                        {/* User avatar — matches the dashboard's profile icon */}
                                        <Tooltip title="Open profile menu">
                                            <Button
                                                onClick={handleOpenUserMenu}
                                                sx={{
                                                    ml: 2,
                                                    px: 1,
                                                    py: 0.5,
                                                    textTransform: 'none',
                                                    color: '#111827',
                                                    borderRadius: '10px',
                                                    '&:hover': { backgroundColor: '#f3f4f6' },
                                                }}
                                                endIcon={
                                                    <KeyboardArrowDownIcon
                                                        sx={{
                                                            color: '#9ca3af',
                                                            transform: Boolean(anchorElUser) ? 'rotate(180deg)' : 'rotate(0deg)',
                                                            transition: 'transform 0.2s',
                                                        }}
                                                    />
                                                }
                                            >
                                                <Box
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: '50%',
                                                        background: 'linear-gradient(135deg, #0AAAE6, #6428AA)',
                                                        color: '#fff',
                                                        fontWeight: 700,
                                                        fontSize: '15px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        mr: 1,
                                                        border: Boolean(anchorElUser) ? '2px solid #0AAAE6' : '2px solid transparent',
                                                        transition: 'border-color 0.2s',
                                                    }}
                                                >
                                                    {(currentUser?.name || 'U').charAt(0).toUpperCase()}
                                                </Box>
                                                <Typography
                                                    sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '14px', fontWeight: 600 }}
                                                >
                                                    {currentUser?.name || ''}
                                                </Typography>
                                            </Button>
                                        </Tooltip>
                                        <Menu
                                            sx={{
                                                mt: '45px',
                                                '& .MuiPaper-root': {
                                                    minWidth: 220,
                                                    borderRadius: '10px',
                                                    overflow: 'hidden',
                                                    border: '1px solid #e5e7eb',
                                                    boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                                                },
                                                '& .MuiList-root': { padding: 0 }
                                            }}
                                            id="menu-appbar"
                                            anchorEl={anchorElUser}
                                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                            keepMounted
                                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                            open={Boolean(anchorElUser)}
                                            onClose={handleCloseUserMenu}
                                        >
                                            {/* Blue header */}
                                            <Box sx={{ px: 2, py: 1.5, backgroundColor: '#0AAAE6', outline: 'none' }}>
                                                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                                                    Hi, {(currentUser?.name || '').toUpperCase()}
                                                </Typography>
                                                <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px' }}>
                                                    {currentUser?.email || ''}
                                                </Typography>
                                            </Box>

                                            <MenuItem
                                                component={Link}
                                                to={dashboardPath}
                                                onClick={handleCloseUserMenu}
                                                sx={{ py: 1.3, px: 2, borderTop: '1px solid #f3f4f6', gap: 1.25, fontSize: '14px', fontWeight: 500, color: '#374151' }}
                                            >
                                                <HomeOutlinedIcon sx={{ fontSize: 18, color: '#6b7280' }} /> Dashboard
                                            </MenuItem>
                                            <MenuItem
                                                component={Link}
                                                to={profilePath}
                                                onClick={handleCloseUserMenu}
                                                sx={{ py: 1.3, px: 2, borderTop: '1px solid #f3f4f6', gap: 1.25, fontSize: '14px', fontWeight: 500, color: '#374151' }}
                                            >
                                                <PersonOutlineIcon sx={{ fontSize: 18, color: '#6b7280' }} /> My Profile
                                            </MenuItem>
                                            <MenuItem
                                                onClick={() => { handleCloseUserMenu(); setChangePasswordOpen(true); }}
                                                sx={{ py: 1.3, px: 2, borderTop: '1px solid #f3f4f6', gap: 1.25, fontSize: '14px', fontWeight: 500, color: '#374151' }}
                                            >
                                                <LockOutlinedIcon sx={{ fontSize: 18, color: '#6b7280' }} /> Change Password
                                            </MenuItem>
                                            <MenuItem
                                                onClick={() => { logout(); handleCloseUserMenu(); navigate('/'); }}
                                                sx={{
                                                    py: 1.3,
                                                    px: 2,
                                                    borderTop: '1px solid #f3f4f6',
                                                    gap: 1.25,
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    color: '#ef4444',
                                                    '&:hover': { backgroundColor: '#fff0f0' }
                                                }}
                                            >
                                                <PowerSettingsNewIcon sx={{ fontSize: 18, color: '#ef4444' }} /> Logout
                                            </MenuItem>
                                        </Menu>
                                </Box>
                            )}
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Renders only for a signed-in job-seeker with an unverified email. */}
            <EmailVerifyBanner />

            {/* Mobile drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true, //Better open performance on mobile.
                }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    // Must match the drawer content's width (280) — leaving the
                    // paper at 240 would clip the wider rows. Capped at 85vw so it
                    // never swallows a 320px screen whole.
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280, maxWidth: '85vw' },
                }}
            >
                {drawer}
            </Drawer>

            {/* Change Password Modal — matches the dashboard's header */}
            <ChangePasswordModal
                isOpen={changePasswordOpen}
                onClose={() => setChangePasswordOpen(false)}
            />
        </>
    );
};

export default Header;